<template>
  <form :class="{ 'discount-campaign-form--embedded': embedded }">
    <div class="category-top-bar sticky-header" v-if="!embedded">
      <div class="top-bar">
        <p>
          <a class="btn btn-orange btn-icon back" :title="$t('general.back')" @click.prevent="backButton">
            <i class="ti-arrow-left"></i>
            {{ $t('general.back') }}
          </a>
        </p>
        <p class="d-inline-flex">
          <a class="btn btn-danger btn-icon btn mar-rt-10 delete" v-if="id" @click.prevent="deleteCampaign">
            <img src="/static/img/icons/icon-close.png">
            {{ $t('general.delete') }}
          </a>
          <button type="submit" class="btn btn-success btn-icon mar-rt-10 save" @click.prevent="validate" :title="$t('general.save')">
            <img src="/static/img/icons/save.png">
            {{ $t('general.save') }}
          </button>
        </p>
      </div>
    </div>

    <div class="row mar-to-15 mar-bt-15 discount-campaign-form" :class="{ 'mar-to-0': embedded }">
      <div class="col-md-12">
        <div class="card discount-campaign-form__card" :class="{ 'discount-campaign-form__card--embedded': embedded }">
          <div :class="(loading ? 'show-loader' : 'hide-loader')">
            <div class="el-loading-spinner">
              <svg viewBox="25 25 50 50" class="circular">
                <circle cx="50" cy="50" r="20" fill="none" class="path"></circle>
              </svg>
            </div>
          </div>
          <div class="card-content">
            <div class="header discount-campaign-form__header" v-if="!embedded">
              <h5>{{ $t('discountCampaigns.details') }}</h5>
            </div>
            <p class="text-danger" v-if="errors.has('error')">{{ errors.first('error') }}</p>

            <div class="row">
              <div class="col-lg-6 col-md-12">
                <div class="form-group">
                  <label>{{ $t('discountCampaigns.name') }} *</label>
                  <input type="text"
                         name="name"
                         v-model="model.name"
                         v-validate="modelValidations.name"
                         class="form-control">
                  <small class="text-danger" v-if="errors.has('name')">{{ errors.first('name') }}</small>
                </div>

                <div class="form-group">
                  <label>{{ $t('discountCampaigns.description') }}</label>
                  <textarea rows="3" v-model="model.description" class="form-control"></textarea>
                </div>

                <div class="form-group">
                  <label>{{ $t('discountCampaigns.image') }}</label>
                  <el-upload
                    v-if="!loading"
                    action=""
                    ref="upload"
                    :limit="2"
                    :file-list="model.imagelist"
                    list-type="picture-card"
                    accept=".jpg, .jpeg, .png"
                    :on-change="handleImgChange"
                    :on-exceed="handleExceed"
                    :on-remove="handleRemove"
                    :auto-upload="false">
                    <img src="/static/img/icons/icon-plus-primary.png">
                  </el-upload>
                </div>

                <div class="row">
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>{{ $t('discountCampaigns.discount') }} *</label>
                      <input type="number"
                             min="0"
                             step="0.01"
                             name="discount"
                             v-model="model.discount"
                             v-validate="modelValidations.discount"
                             class="form-control"
                             @input="schedulePreview">
                      <small class="text-danger" v-if="errors.has('discount')">{{ errors.first('discount') }}</small>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>{{ $t('discountCampaigns.discountType') }} *</label>
                      <el-select v-model="model.discount_type" style="width: 100%;" @change="schedulePreview">
                        <el-option
                          v-for="type in discountTypes"
                          :key="type.id"
                          :label="$t('discountCampaigns.discountType_' + type.key)"
                          :value="type.id">
                        </el-option>
                      </el-select>
                    </div>
                  </div>
                </div>

                <div class="form-group">
                  <label>{{ $t('discountCampaigns.numberOfUnits') }}</label>
                  <input type="number"
                         min="1"
                         name="number_of_units"
                         v-model="model.number_of_units"
                         class="form-control"
                         :placeholder="$t('discountCampaigns.numberOfUnitsPlaceholder')"
                         @input="schedulePreview">
                  <small class="text-muted">{{ $t('discountCampaigns.numberOfUnitsHint') }}</small>
                </div>

                <div class="row" v-if="model.number_of_units">
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>{{ $t('discountCampaigns.unitsSoldScope') }}</label>
                      <el-select v-model="model.units_sold_scope" style="width: 100%;" @change="schedulePreview">
                        <el-option
                          v-for="scope in unitsSoldScopes"
                          :key="scope.id"
                          :label="$t('discountCampaigns.unitsSoldScope_' + scope.key)"
                          :value="scope.id">
                        </el-option>
                      </el-select>
                      <small class="text-muted">{{ $t('discountCampaigns.unitsSoldScopeHint') }}</small>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="form-group">
                      <label>{{ $t('discountCampaigns.unitsSoldPool') }}</label>
                      <el-select v-model="model.units_sold_pool" style="width: 100%;" @change="schedulePreview">
                        <el-option
                          v-for="pool in unitsSoldPools"
                          :key="pool.id"
                          :label="$t('discountCampaigns.unitsSoldPool_' + pool.key)"
                          :value="pool.id">
                        </el-option>
                      </el-select>
                      <small class="text-muted">{{ $t('discountCampaigns.unitsSoldPoolHint') }}</small>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-lg-6 col-md-12">
                <div class="form-group">
                  <label>
                    {{ $t('discountCampaigns.priceProfile') }} *
                    <i v-if="lockPriceProfile" class="ti-lock text-muted" :title="$t('discountCampaigns.priceProfileLocked')"></i>
                  </label>
                  <el-select
                    v-model="model.price_profile_id"
                    filterable
                    style="width: 100%;"
                    :placeholder="$t('general.select')"
                    :disabled="lockPriceProfile"
                    @change="schedulePreview">
                    <el-option
                      v-for="profile in priceProfiles"
                      :key="profile.id"
                      :label="profile.name"
                      :value="profile.id">
                    </el-option>
                  </el-select>
                  <small class="text-danger" v-if="errors.has('price_profile_id')">{{ errors.first('price_profile_id') }}</small>
                </div>

                <div class="form-group">
                  <label>{{ $t('discountCampaigns.sourceMethodCombos') }} *</label>
                  <el-select
                    v-model="selectedComboKeys"
                    class="combo-select"
                    multiple
                    filterable
                    collapse-tags
                    style="width: 100%;"
                    :placeholder="$t('general.select')"
                    @change="onSourceMethodCombosChange">
                    <el-option
                      v-for="option in sourceMethodOptions"
                      :key="option.key"
                      :label="option.label"
                      :value="option.key">
                    </el-option>
                  </el-select>
                  <small class="text-danger" v-if="errors.has('source_method_combos')">{{ errors.first('source_method_combos') }}</small>
                </div>

                <searchable-multi-select
                  :value="selectedProducts"
                  :label="'discountCampaigns.products'"
                  url="/search-order-products"
                  :initial-limit="0"
                  @input="onProductsChange"
                />

                <searchable-multi-select
                  :value="selectedTopCategories"
                  :label="'discountCampaigns.topCategories'"
                  url="/search-top-categories"
                  :initial-limit="10"
                  @input="onTopCategoriesChange"
                />

                <searchable-multi-select
                  :key="'categories-' + topCategoriesFilterKey"
                  :value="selectedCategories"
                  :label="'pages.categories'"
                  url="/search-categories"
                  :initial-limit="10"
                  :extra-params="categorySearchParams"
                  @input="onCategoriesChange"
                />

                <div class="form-group">
                  <label>{{ $t('discountCampaigns.startEnd') }} *</label>
                  <el-date-picker
                    type="datetimerange"
                    v-model="model.fromto"
                    format="yyyy-MM-dd HH:mm"
                    value-format="yyyy-MM-dd HH:mm:ss"
                    style="width: 100%;">
                  </el-date-picker>
                  <small class="text-danger" v-if="errors.has('start_date')">{{ errors.first('start_date') }}</small>
                </div>

                <div class="form-group">
                  <label>{{ $t('discountCampaigns.active') }}</label>
                  <p-switch v-model="model.active" type="success"></p-switch>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-12">
                <div class="form-group use-intervals-toggle">
                  <label>{{ $t('discountCampaigns.useIntervals') }}</label>
                  <div class="use-intervals-toggle__switch">
                    <p-switch v-model="useIntervals" type="success"></p-switch>
                  </div>
                  <small class="text-muted use-intervals-toggle__hint">{{ $t('discountCampaigns.useIntervalsHint') }}</small>
                </div>

                <template v-if="useIntervals">
                  <div class="intervals-block">
                    <div class="intervals-row intervals-row--header">
                      <div class="intervals-row__day">{{ $t('discountCampaigns.intervalDay') }}</div>
                      <div class="intervals-row__from">{{ $t('general.from') }}</div>
                      <div class="intervals-row__to">{{ $t('general.to') }}</div>
                      <div class="intervals-row__actions" aria-hidden="true"></div>
                    </div>
                    <div
                      v-for="(interval, index) in model.intervals"
                      :key="'interval-' + index"
                      class="intervals-row">
                      <div class="intervals-row__day">
                        <el-select
                          class="interval-day-select"
                          :value="intervalDaySelectValue(interval.day)"
                          :placeholder="$t('general.all')"
                          @input="setIntervalDay(interval, $event)">
                          <el-option
                            v-for="opt in intervalDayOptions"
                            :key="'day-' + String(opt.value)"
                            :label="opt.label"
                            :value="opt.value">
                            <span>{{ opt.label }}</span>
                          </el-option>
                        </el-select>
                      </div>
                      <div class="intervals-row__from">
                        <el-time-select
                          class="interval-time-select"
                          v-model="interval.time_from"
                          :placeholder="$t('general.from')"
                          :picker-options="timePickerOptions">
                        </el-time-select>
                      </div>
                      <div class="intervals-row__to">
                        <el-time-select
                          class="interval-time-select"
                          v-model="interval.time_to"
                          :placeholder="$t('general.to')"
                          :picker-options="timePickerOptions">
                        </el-time-select>
                      </div>
                      <div class="intervals-row__actions">
                        <button
                          type="button"
                          class="interval-remove"
                          :title="$t('general.delete')"
                          @click.prevent="removeInterval(index)">
                          <i class="ti-close"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                  <small class="text-danger intervals-error" v-if="errors.has('intervals')">{{ errors.first('intervals') }}</small>
                  <button type="button" class="btn btn-default btn-sm interval-add" @click.prevent="addInterval">
                    <i class="ti-plus"></i> {{ $t('discountCampaigns.addInterval') }}
                  </button>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-md-12" v-if="canPreview && !hidePreview">
        <div class="card">
          <div class="card-content">
            <div class="header" style="margin-bottom: 15px;">
              <h5>{{ $t('discountCampaigns.previewTitle') }}</h5>
              <small class="text-muted">
                {{ $t('discountCampaigns.previewCount', { count: gridProductsCount }) }}
              </small>
            </div>

            <div class="row filters-wrapper" style="margin-bottom: 15px;">
              <div class="col-md-4">
                <div class="form-group">
                  <label>{{ $t('discountCampaigns.searchProduct') }}</label>
                  <input type="text" class="form-control" v-model="previewFilters.name" @input="schedulePreview">
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label>{{ $t('discountCampaigns.searchProductId') }}</label>
                  <input type="number" class="form-control" v-model="previewFilters.product_id" @input="schedulePreview">
                </div>
              </div>
            </div>

            <discount-campaign-pricing-grid
              :products="gridProducts"
              :pricing-data="gridPricingData"
              :combos="gridCombos"
              :loading="previewLoading"
              :show-units-sold="!!id"
              :pagination="gridPagination"
              @page-change="onPreviewPageChange"
            />
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import Vue from 'vue'
import { Upload, Button, DatePicker, Select, Option, TimeSelect } from 'element-ui'
import PSwitch from 'src/components/UIComponents/Switch.vue'
import SearchableMultiSelect from 'src/components/UIComponents/Inputs/SearchableMultiSelect.vue'
import DiscountCampaignPricingGrid from './DiscountCampaignPricingGrid.vue'
import vSelect from 'vue-select'
import swal from 'sweetalert2'
import 'sweetalert2/dist/sweetalert2.css'
import {
  DISCOUNT_TYPES,
  UNITS_SOLD_SCOPES,
  UNITS_SOLD_POOLS,
  INTERVAL_DAYS,
  combosToPayload,
  mergeComboOptions,
  resolveSelectedCombos,
  sortCombosLikePriceProfile
} from './discountCampaignConstants'

Vue.component('v-select', vSelect)
Vue.use(DatePicker)
Vue.use(Upload)
Vue.use(Button)
Vue.use(Select)
Vue.use(Option)
Vue.use(TimeSelect)

export default {
  components: {
    PSwitch,
    SearchableMultiSelect,
    DiscountCampaignPricingGrid
  },
  props: {
    route: {
      type: Object,
      default: () => ({})
    },
    embedded: {
      type: Boolean,
      default: false
    },
    hidePreview: {
      type: Boolean,
      default: false
    },
    lockPriceProfile: {
      type: Boolean,
      default: false
    },
    prefill: {
      type: Object,
      default: null
    }
  },
  data () {
    return {
      id: (this.route && this.route.params && this.route.params.id) || null,
      loading: true,
      previewLoading: false,
      previewTimeout: null,
      previewDebounceMs: 2000,
      useIntervals: false,
      discountTypes: DISCOUNT_TYPES,
      unitsSoldScopes: UNITS_SOLD_SCOPES,
      unitsSoldPools: UNITS_SOLD_POOLS,
      priceProfiles: [],
      sourceMethodOptions: [],
      selectedSourceMethodCombos: [],
      selectedComboKeys: [],
      selectedCategories: [],
      selectedTopCategories: [],
      selectedProducts: [],
      lineAffectingSnapshot: null,
      model: {
        name: '',
        description: '',
        image: null,
        imagelist: [],
        active: false,
        discount: null,
        discount_type: 2,
        price_profile_id: null,
        source_method_combos: [],
        category_ids: [],
        top_category_ids: [],
        product_ids: [],
        number_of_units: null,
        units_sold_scope: 1,
        units_sold_pool: 1,
        fromto: [],
        intervals: []
      },
      modelValidations: {
        name: { required: true, min: 3 },
        discount: { required: true, min_value: 0 }
      },
      timePickerOptions: {
        start: '00:00',
        step: '00:15',
        end: '23:45'
      },
      gridProducts: [],
      gridPricingData: {},
      gridCombos: [],
      gridProductsCount: 0,
      previewFilters: {
        name: '',
        product_id: ''
      },
      gridPagination: {
        page: 1,
        perPage: 20,
        totalPages: 0,
        totalRecords: 0
      }
    }
  },
  computed: {
    canPreview () {
      return this.model.price_profile_id &&
        this.selectedSourceMethodCombos.length > 0 &&
        this.model.discount != null &&
        this.model.discount !== ''
    },
    intervalDayOptions () {
      const cap = (value) => {
        if (!value) return ''
        const text = String(value)
        return text.charAt(0).toUpperCase() + text.slice(1)
      }
      return [
        { value: '', label: cap(this.$t('general.all')) },
        ...INTERVAL_DAYS.map(day => ({
          value: day,
          label: cap(this.$t('days.' + day))
        }))
      ]
    },
    topCategoriesFilterKey () {
      const ids = this.model.top_category_ids || []
      return ids.length ? ids.slice().sort((a, b) => a - b).join(',') : 'all'
    },
    categorySearchParams () {
      const params = {}
      if (this.model.top_category_ids && this.model.top_category_ids.length) {
        params.top_category_ids = this.model.top_category_ids
      }
      return params
    }
  },
  watch: {
    useIntervals (value) {
      if (!value) {
        this.model.intervals = []
      } else if (!this.model.intervals.length) {
        this.addInterval()
      }
    }
  },
  beforeDestroy () {
    clearTimeout(this.previewTimeout)
  },
  created () {
    Promise.all([
      this.loadPriceProfiles(),
      this.loadSourceMethodOptions()
    ]).then(() => {
      if (this.id) {
        this.loadCampaign()
      } else {
        this.applyPrefill()
        this.loading = false
      }
    }).catch(() => {
      this.loading = false
    })
  },
  methods: {
    normalizeIntervalTime (value) {
      if (!value) return ''
      const parts = String(value).trim().split(':')
      if (parts.length >= 2) {
        const hours = parts[0].padStart(2, '0')
        const minutes = parts[1].padStart(2, '0')
        return `${hours}:${minutes}`
      }
      return String(value).trim()
    },
    normalizeIntervalDay (day) {
      if (day == null || day === '') return null
      if (typeof day === 'object') {
        return day.value != null && day.value !== '' ? day.value : null
      }
      return day
    },
    intervalDaySelectValue (day) {
      const value = this.normalizeIntervalDay(day)
      return value == null ? '' : value
    },
    setIntervalDay (interval, value) {
      interval.day = value === '' || value == null ? null : value
    },
    mapEntityOptions (entities, prefix) {
      return (entities || []).map(entity => ({
        id: entity.id,
        label: entity.label || entity.name || `${prefix || ''}#${entity.id}`
      }))
    },
    // Selected combos are kept both as objects (payload/preview) and as keys (el-select).
    setSelectedCombos (combos) {
      this.sourceMethodOptions = mergeComboOptions(this.sourceMethodOptions, combos)
      this.selectedSourceMethodCombos = resolveSelectedCombos(this.sourceMethodOptions, combos)
      this.selectedComboKeys = this.selectedSourceMethodCombos.map(combo => combo.key)
      this.model.source_method_combos = combosToPayload(this.selectedSourceMethodCombos)
    },
    // The campaign payload already carries the profile name, so the select shows it
    // even when the profile is missing from the paginated /price-profiles list.
    seedPriceProfile (profile) {
      if (!profile || profile.id == null) return
      const exists = this.priceProfiles.some(item => String(item.id) === String(profile.id))
      if (!exists) {
        this.priceProfiles = [
          { id: profile.id, name: profile.name || `#${profile.id}` },
          ...this.priceProfiles
        ]
      }
      const match = this.priceProfiles.find(item => String(item.id) === String(this.model.price_profile_id))
      if (match) {
        this.model.price_profile_id = match.id
      }
    },
    applyPrefill () {
      if (!this.prefill) return
      if (this.prefill.price_profile_id) {
        this.model.price_profile_id = this.prefill.price_profile_id
      }
      if (this.prefill.source_method_combos && this.prefill.source_method_combos.length) {
        this.setSelectedCombos(this.prefill.source_method_combos)
      }
      if (this.prefill.products && this.prefill.products.length) {
        this.selectedProducts = this.mapEntityOptions(this.prefill.products)
        this.model.product_ids = this.selectedProducts.map(p => p.id)
      } else if (this.prefill.product_ids && this.prefill.product_ids.length) {
        this.selectedProducts = this.prefill.product_ids.map(id => ({
          id,
          label: `#${id}`
        }))
        this.model.product_ids = this.prefill.product_ids.slice()
      }
    },
    loadPriceProfiles () {
      return new Promise((resolve) => {
        const data = {
          token: this.$session.get('token'),
          company_id: this.$session.get('selectedCompany'),
          perPage: 500,
          page: 1
        }
        this.apiRequest('/price-profiles', 'GET', data, (response) => {
          if (response.status) {
            this.priceProfiles = Array.isArray(response.data) ? response.data : (response.data && response.data.data) || []
          }
          resolve()
        })
      })
    },
    loadSourceMethodOptions () {
      return new Promise((resolve) => {
        const data = {
          token: this.$session.get('token'),
          company_id: this.$session.get('selectedCompany')
        }
        this.apiRequest('/discount-campaigns/source-method-options', 'GET', data, (response) => {
          if (response.status) {
            this.sourceMethodOptions = mergeComboOptions(response.data, this.selectedSourceMethodCombos)
          }
          resolve()
        })
      })
    },
    applyGridResponse (payload) {
      const grid = payload || {}
      this.gridProducts = grid.products || grid.data || []
      this.gridPricingData = grid.pricingData || {}
      this.gridCombos = sortCombosLikePriceProfile(grid.combos || [])
      this.gridProductsCount = grid.products_count != null ? grid.products_count : this.gridProducts.length
      if (grid.pagination) {
        this.gridPagination.page = grid.pagination.page
        this.gridPagination.perPage = grid.pagination.perPage
        this.gridPagination.totalPages = grid.pagination.totalPages
        this.gridPagination.totalRecords = grid.pagination.totalRecords
      }
    },
    loadCampaign () {
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany')
      }
      this.apiRequest('/discount-campaigns/' + this.id, 'GET', data, (response) => {
        if (response.status === true) {
          const item = response.data
          if (item.start_date && item.end_date) {
            item.fromto = [item.start_date, item.end_date]
          } else {
            item.fromto = []
          }
          item.active = !!(parseInt(item.active, 10) === 1 || item.active === true)
          if (item.image) {
            item.imagelist = [{ url: item.image }]
          } else {
            item.imagelist = []
          }
          if (!item.intervals) {
            item.intervals = []
          }
          item.intervals = item.intervals.map(interval => ({
            day: interval.day || null,
            time_from: this.normalizeIntervalTime(interval.time_from || interval.from || ''),
            time_to: this.normalizeIntervalTime(interval.time_to || interval.to || '')
          }))
          this.useIntervals = item.intervals.length > 0
          this.id = item.id || this.id
          this.model = item
          this.model.units_sold_scope = parseInt(item.units_sold_scope, 10) || 1
          this.model.units_sold_pool = parseInt(item.units_sold_pool, 10) || 1

          this.seedPriceProfile(item.price_profile)

          // `combos` carries the labels for the saved combos, so no extra lookup is needed.
          this.sourceMethodOptions = mergeComboOptions(this.sourceMethodOptions, item.combos || [])
          this.setSelectedCombos(
            (item.source_method_combos && item.source_method_combos.length)
              ? item.source_method_combos
              : (item.combos || [])
          )

          if (item.top_categories_formatted && item.top_categories_formatted.length) {
            this.selectedTopCategories = item.top_categories_formatted
          } else if (item.top_categories && item.top_categories.length) {
            this.selectedTopCategories = this.mapEntityOptions(item.top_categories, 'Top category ')
          } else if (item.top_category_ids && item.top_category_ids.length) {
            this.selectedTopCategories = item.top_category_ids.map(id => ({ id, label: `Top category #${id}` }))
          } else if (item.top_category_formatted) {
            this.selectedTopCategories = [item.top_category_formatted]
          } else if (item.top_category_id) {
            this.selectedTopCategories = [{
              id: item.top_category_id,
              label: item.top_category_label || `Top category #${item.top_category_id}`
            }]
          } else {
            this.selectedTopCategories = []
          }
          this.model.top_category_ids = this.selectedTopCategories.map(cat => cat.id)

          if (item.categories_formatted && item.categories_formatted.length) {
            this.selectedCategories = item.categories_formatted
          } else if (item.categories && item.categories.length) {
            this.selectedCategories = this.mapEntityOptions(item.categories, 'Category ')
          } else if (item.category_ids && item.category_ids.length) {
            this.selectedCategories = item.category_ids.map(id => ({ id, label: `Category #${id}` }))
          }
          this.model.category_ids = (this.selectedCategories || []).map(cat => cat.id)

          if (item.products_formatted && item.products_formatted.length) {
            this.selectedProducts = item.products_formatted
          } else if (item.products && item.products.length) {
            this.selectedProducts = this.mapEntityOptions(item.products)
          } else if (item.product_ids && item.product_ids.length) {
            this.selectedProducts = item.product_ids.map(id => ({ id, label: `#${id}` }))
          } else {
            this.selectedProducts = []
          }
          this.model.product_ids = (this.selectedProducts || []).map(p => p.id)

          this.applyGridResponse(item)
          this.lineAffectingSnapshot = this.getLineAffectingSnapshot()
        }
        this.$nextTick(() => {
          this.loading = false
        })
      })
    },
    onSourceMethodCombosChange (keys) {
      this.selectedComboKeys = keys || []
      this.selectedSourceMethodCombos = this.selectedComboKeys
        .map(key => this.sourceMethodOptions.find(option => option.key === key))
        .filter(Boolean)
      this.model.source_method_combos = combosToPayload(this.selectedSourceMethodCombos)
      this.schedulePreview()
    },
    onTopCategoriesChange (selected) {
      const previousKey = (this.model.top_category_ids || []).slice().sort((a, b) => a - b).join(',')
      this.selectedTopCategories = selected || []
      this.model.top_category_ids = this.selectedTopCategories.map(cat => cat.id)
      const currentKey = this.model.top_category_ids.slice().sort((a, b) => a - b).join(',')
      if (previousKey !== currentKey) {
        this.selectedCategories = []
        this.model.category_ids = []
      }
      this.schedulePreview()
    },
    onCategoriesChange (selected) {
      this.selectedCategories = selected || []
      this.model.category_ids = this.selectedCategories.map(cat => cat.id)
      this.schedulePreview()
    },
    onProductsChange (selected) {
      this.selectedProducts = selected || []
      this.model.product_ids = this.selectedProducts.map(p => p.id)
      this.schedulePreview()
    },
    addInterval () {
      this.model.intervals.push({
        day: null,
        time_from: '10:00',
        time_to: '14:00'
      })
    },
    removeInterval (index) {
      this.model.intervals.splice(index, 1)
    },
    timeToMinutes (value) {
      if (!/^(?:[01][0-9]|2[0-3]):[0-5][0-9](?::[0-5][0-9])?$/.test(String(value || ''))) return null
      const parts = String(value).split(':').map(Number)
      return parts[0] * 60 + parts[1] + (parts[2] || 0) / 60
    },
    intervalWindows (interval) {
      const start = this.timeToMinutes(interval.time_from)
      const end = this.timeToMinutes(interval.time_to)
      if (start === null || end === null || start === end) return []
      const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
      const day = this.normalizeIntervalDay(interval.day)
      const selectedDays = day === null ? [0, 1, 2, 3, 4, 5, 6] : [days.indexOf(String(day).toLowerCase())]
      return selectedDays.map(index => [index * 1440 + start, index * 1440 + end + (end < start ? 1440 : 0)])
    },
    intervalsConflict (first, second) {
      const firstWindows = this.intervalWindows(first)
      const secondWindows = this.intervalWindows(second)
      return firstWindows.some(a => secondWindows.some(b =>
        [-10080, 0, 10080].some(offset => a[0] < b[1] + offset && b[0] + offset < a[1])
      ))
    },
    validateIntervals () {
      if (!this.useIntervals) return true
      const intervals = this.model.intervals || []
      for (let i = 0; i < intervals.length; i++) {
        const from = this.timeToMinutes(intervals[i].time_from)
        const to = this.timeToMinutes(intervals[i].time_to)
        if (from === null || to === null || from === to) {
          this.errors.add('intervals', this.$t('discountCampaigns.intervalInvalidRange'))
          return false
        }
      }
      for (let i = 0; i < intervals.length; i++) {
        for (let j = i + 1; j < intervals.length; j++) {
          if (this.intervalsConflict(intervals[i], intervals[j])) {
            this.errors.add('intervals', this.$t('discountCampaigns.intervalOverlap'))
            return false
          }
        }
      }
      return true
    },
    schedulePreview () {
      clearTimeout(this.previewTimeout)
      if (this.hidePreview || !this.canPreview) {
        this.gridProducts = []
        this.gridPricingData = {}
        this.gridCombos = []
        this.gridProductsCount = 0
        return
      }
      this.previewTimeout = setTimeout(() => {
        this.gridPagination.page = 1
        this.fetchPreview()
      }, this.previewDebounceMs)
    },
    onPreviewPageChange (page) {
      this.gridPagination.page = page
      this.fetchPreview()
    },
    getCampaignId () {
      const raw = this.id ||
        (this.model && this.model.id) ||
        (this.route && this.route.params && this.route.params.id) ||
        null
      if (raw == null || raw === '') return null
      const parsed = parseInt(raw, 10)
      return Number.isNaN(parsed) ? null : parsed
    },
    buildPreviewPayload () {
      const payload = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        discount: parseFloat(this.model.discount),
        discount_type: parseInt(this.model.discount_type, 10),
        price_profile_id: this.model.price_profile_id,
        source_method_combos: this.model.source_method_combos || [],
        category_ids: this.model.category_ids || [],
        top_category_ids: this.model.top_category_ids || [],
        product_ids: this.model.product_ids || [],
        number_of_units: this.model.number_of_units ? parseInt(this.model.number_of_units, 10) : null,
        units_sold_scope: parseInt(this.model.units_sold_scope, 10) || 1,
        units_sold_pool: parseInt(this.model.units_sold_pool, 10) || 1,
        page: this.gridPagination.page,
        perPage: this.gridPagination.perPage
      }
      const campaignId = this.getCampaignId()
      if (campaignId != null) {
        payload.id = campaignId
      }
      if (this.previewFilters.name) {
        payload.name = this.previewFilters.name
      }
      if (this.previewFilters.product_id) {
        payload.product_id = parseInt(this.previewFilters.product_id, 10)
      }
      return payload
    },
    fetchPreview () {
      if (!this.canPreview) return
      this.previewLoading = true
      this.apiRequest('/discount-campaigns/preview', 'POST', this.buildPreviewPayload(), (response) => {
        if (response.status) {
          this.applyGridResponse(response.data || {})
        }
        this.previewLoading = false
      })
    },
    getLineAffectingSnapshot () {
      return JSON.stringify({
        discount: String(this.model.discount),
        discount_type: parseInt(this.model.discount_type, 10),
        price_profile_id: this.model.price_profile_id,
        source_method_combos: [...(this.model.source_method_combos || [])].map(c => `${c.source}_${c.method}`).sort(),
        category_ids: [...(this.model.category_ids || [])].sort(),
        top_category_ids: [...(this.model.top_category_ids || [])].sort((a, b) => a - b),
        product_ids: [...(this.model.product_ids || [])].sort((a, b) => a - b),
        number_of_units: this.model.number_of_units ? parseInt(this.model.number_of_units, 10) : null,
        units_sold_scope: parseInt(this.model.units_sold_scope, 10) || 1,
        units_sold_pool: parseInt(this.model.units_sold_pool, 10) || 1
      })
    },
    willRegenerateLines () {
      if (!this.id || !this.lineAffectingSnapshot) return false
      return this.getLineAffectingSnapshot() !== this.lineAffectingSnapshot
    },
    validate () {
      this.errors.clear()
      if (!this.model.price_profile_id) {
        this.errors.add('price_profile_id', this.$t('validations.requiredField'))
      }
      if (!this.selectedSourceMethodCombos.length) {
        this.errors.add('source_method_combos', this.$t('validations.requiredField'))
      }
      if (!this.model.fromto || this.model.fromto.length !== 2) {
        this.errors.add('start_date', this.$t('validations.requiredField'))
      }
      this.validateIntervals()
      this.$validator.validateAll().then(isValid => {
        if (isValid &&
          !this.errors.has('price_profile_id') &&
          !this.errors.has('source_method_combos') &&
          !this.errors.has('start_date') &&
          !this.errors.has('intervals')) {
          if (this.willRegenerateLines()) {
            swal({
              text: this.$t('discountCampaigns.regenerateWarning'),
              type: 'warning',
              showCancelButton: true,
              confirmButtonText: this.$t('general.yes')
            }).then((result) => {
              if (result) {
                this.saveCampaign()
              }
            }).catch(() => {})
          } else {
            this.saveCampaign()
          }
        }
      })
    },
    buildSavePayload () {
      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        name: this.model.name,
        description: this.model.description || '',
        active: this.model.active ? 1 : 0,
        discount: parseFloat(this.model.discount),
        discount_type: parseInt(this.model.discount_type, 10),
        price_profile_id: this.model.price_profile_id,
        source_method_combos: this.model.source_method_combos || [],
        category_ids: this.model.category_ids || [],
        top_category_ids: this.model.top_category_ids || [],
        product_ids: this.model.product_ids || [],
        number_of_units: this.model.number_of_units ? parseInt(this.model.number_of_units, 10) : null,
        units_sold_scope: parseInt(this.model.units_sold_scope, 10) || 1,
        units_sold_pool: parseInt(this.model.units_sold_pool, 10) || 1
      }
      if (this.model.fromto && this.model.fromto[0] && this.model.fromto[1]) {
        data.start_date = this.model.fromto[0]
        data.end_date = this.model.fromto[1]
      }
      if (this.model.image) {
        data.image = this.model.image
      } else if (Array.isArray(this.model.imagelist) && this.model.imagelist.length === 0) {
        data.image = ''
      }
      if (this.useIntervals && this.model.intervals.length) {
        data.intervals = this.model.intervals.map(interval => ({
          day: this.normalizeIntervalDay(interval.day),
          time_from: this.normalizeIntervalTime(interval.time_from),
          time_to: this.normalizeIntervalTime(interval.time_to)
        }))
      } else {
        data.intervals = []
      }
      return data
    },
    saveCampaign () {
      this.loading = true
      const data = this.buildSavePayload()
      const url = this.id ? '/discount-campaigns/' + this.id : '/discount-campaigns'
      const method = this.id ? 'PUT' : 'POST'
      this.apiRequest(url, method, data, (response) => {
        if (response.status === true) {
          this.loading = false
          swal({
            title: '',
            text: this.id ? this.$t('discountCampaigns.campaignUpdated') : this.$t('discountCampaigns.campaignAdded'),
            type: 'success',
            showConfirmButton: false,
            timer: 1500
          }).catch(() => {})
          this.backButton()
        } else {
          this.loading = false
          if (response.errors) {
            this.handleErrors(response.errors)
          } else {
            swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
          }
        }
      })
    },
    handleErrors (errors) {
      for (const prop in errors) {
        if (Object.prototype.hasOwnProperty.call(errors, prop)) {
          const msg = Array.isArray(errors[prop]) ? errors[prop][0] : errors[prop]
          this.errors.add(prop, msg)
        }
      }
    },
    deleteCampaign () {
      swal({
        text: this.$t('discountCampaigns.areYouSureDelete') + ' ' + (this.model.name || '') + '?',
        type: 'question',
        showCancelButton: true,
        confirmButtonText: this.$t('general.yes')
      }).then((result) => {
        if (result) {
          const data = {
            token: this.$session.get('token'),
            company_id: this.$session.get('selectedCompany')
          }
          this.apiRequest('/discount-campaigns/' + this.id, 'DELETE', data, (response) => {
            if (response.status === true) {
              swal('', this.$t('discountCampaigns.campaignDeleted'), 'success').catch(() => {})
              this.backButton()
            } else {
              swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
            }
          })
        }
      }).catch(() => {})
    },
    handleImgChange (file, fileList) {
      const isOkSize = file.size / 1024 / 1024 < 5
      if (!isOkSize) {
        swal({ toast: true, type: 'warning', text: this.$t('validations.pictureSizeExceed'), position: 'top-end', showConfirmButton: false, timer: 3000 })
        this.$refs.upload.clearFiles()
        this.model.imagelist = []
        return
      }
      if (fileList.length > 1) {
        this.model.imagelist = fileList.slice(1)
      } else {
        this.model.imagelist = fileList
      }
      if (this.model.imagelist[0] && this.model.imagelist[0].status === 'ready') {
        this.getBase64(this.model.imagelist[0].raw).then((base64enc) => {
          this.model.image = base64enc
        })
      }
    },
    handleRemove () {
      this.model.imagelist = []
      this.model.image = null
    },
    handleExceed () {
      swal({ text: this.$t('discountCampaigns.removeCurrentImage'), type: 'warning', showConfirmButton: true })
    },
    backButton () {
      if (this.embedded) {
        this.$emit('saved')
        return
      }
      this.$bus.$emit('refresh')
      this.$bus.$emit('close-modal')
    },
    cancelEmbedded () {
      this.$emit('cancel')
    }
  }
}
</script>

<style scoped>
.discount-campaign-form__card {
  width: 100%;
  margin: 0;
}
.discount-campaign-form__card--embedded {
  box-shadow: none;
  border: none;
}
.discount-campaign-form__header {
  margin: -15px 0 30px !important;
}
.use-intervals-toggle__switch {
  display: block;
  margin: 4px 0 6px;
}
.use-intervals-toggle__hint {
  display: block;
  clear: both;
  margin: 0 0 12px;
  width: 100%;
}
.intervals-block {
  max-width: 600px;
  margin-bottom: 12px;
}
.intervals-row {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}
.intervals-row--header {
  margin-bottom: 6px;
}
.intervals-row--header .intervals-row__day,
.intervals-row--header .intervals-row__from,
.intervals-row--header .intervals-row__to {
  font-size: 13px;
  font-weight: 500;
  color: #25396F;
  line-height: 1.2;
  white-space: nowrap;
}
.intervals-row__day {
  flex: 0 0 170px;
  margin-right: 12px;
  min-width: 0;
}
.intervals-row__from {
  flex: 0 0 130px;
  margin-right: 20px;
  min-width: 0;
}
.intervals-row__to {
  flex: 0 0 130px;
  margin-right: 16px;
  min-width: 0;
}
.intervals-row__actions {
  flex: 0 0 46px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.interval-day-select,
.interval-time-select {
  width: 100% !important;
}
.interval-time-select >>> .el-date-editor.el-input {
  width: 100% !important;
}
.interval-day-select >>> .el-input,
.interval-time-select >>> .el-input {
  width: 100% !important;
}
.interval-day-select >>> .el-input__inner,
.interval-time-select >>> .el-input__inner {
  width: 100% !important;
}
.interval-remove {
  box-sizing: border-box;
  width: 46px;
  height: 46px;
  padding: 0;
  margin: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #e74c3c;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  line-height: 1;
  box-shadow: none;
  flex-shrink: 0;
}
.interval-remove:hover,
.interval-remove:focus {
  background: #fdecea;
  color: #c0392b;
  outline: none;
}
.interval-remove i {
  font-size: 16px;
  line-height: 1;
  margin: 0;
}
.interval-add {
  height: 36px;
  padding: 0 12px;
  line-height: 34px;
}
.intervals-error {
  display: block;
  margin: 2px 0 10px;
  max-width: 600px;
}
.mar-to-0 {
  margin-top: 0 !important;
}
.discount-campaign-form--embedded .discount-campaign-form {
  margin-left: 0;
  margin-right: 0;
}
.discount-campaign-form--embedded .card-content {
  padding: 0;
}
.discount-campaign-form--embedded .row {
  margin-left: 0;
  margin-right: 0;
}

.discount-campaign-form >>> .v-select .dropdown-toggle {
  height: auto;
  min-height: 46px;
}

/* Collapsed tags must stay on one line inside the fixed-height Element input. */
.combo-select >>> .el-select__tags {
  flex-wrap: nowrap;
  max-width: calc(100% - 30px) !important;
  overflow: hidden;
}
.combo-select >>> .el-select__tags .el-tag {
  max-width: 100%;
  overflow: hidden;
}
.combo-select >>> .el-select__tags .el-tag > span:first-child {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
  white-space: nowrap;
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Marketing/DiscountCampaigns/DiscountCampaignForm.vue